import React, { useState, useEffect, useRef } from 'react'
import 'url-search-params-polyfill';

import UploadTable from '../elements/userupload/UploadTable';

import { IHttpRequest } from  '@/interfaces/IHttp';
import { container, TYPES } from "../../inversifyconfig/container.config";
import { UserImportFieldOptionsModel } from '../../models/ApiResponseModels/UserImportFieldOptionsModel';
import { UserUploadActionProgress } from '../../models/ApiResponseModels/UserUploadActionProgressModel';

import useSignalR from '../hooks/useSignalR';

import '../../css/userUpload.scss';
import BulkImportUsersModal from '../modals/BulkImportUsersModal';
import UploadDetailsModal from '../modals/UploadDetailsModal';
import { UserUploadHistory } from '../../models/ApiResponseModels/UserUploadHistoryResponseModel';
import ToolTipWrapper from '../elements/Tooltip/ToolTipWrapper';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { UserImportType } from  '@/models/UserImportType';
import { UserImportStep } from  '@/models/UserImportStep';

const UserUpload = () => {
    const httpCall = container.get<IHttpRequest>(TYPES.IHttpRequest)
    const [isOpen, SetUploadModal] = useState(false);
    const [isDetailsOpen, SetDetailsModal] = useState(false);
    const [currentStep, SetStep] = useState(UserImportStep.ImportTypeSelection);
    const [formValid, SetValid] = useState(false);
    const [progressReport, SetProgressReport] = useState<UserUploadActionProgress>(null);
    const [errors, setErrors] = useState<boolean>(false);
    const [responseError, setResponseError] = useState<{ message: string, code: number }>();
    const cancelOrCompleteInFlightMap = useRef<{ [key: number]: 'cancel' | 'complete' }>({});

    useSignalR({
        connectionString: '/hubs/clientnotifications',
        connectionMethods: [
            { method: 'UserUploadNotification', newMethod: (message: UserUploadActionProgress) => processUserUploadNotificationEvent(message) }
        ],
        reconnectCallBack: () => console.log('reconnected'),
        connectionCloseCallback: () => console.log('connection closed'),
    });

    const [userImportOptions, setUserImportOptions] = useState<UserImportFieldOptionsModel[]>(null);
    const [importHistory, setImportHistory] = useState<UserUploadHistory[]>([]);
    const [getFile, setFile] = useState<HTMLInputElement>(null);
    const [loading, setLoading] = useState(true);
    const [uploadType, setUploadType] = useState<UserImportType>(UserImportType.Fullsync);

    const importId = useRef(0);

    useEffect(() => {
        refreshList();
        httpCall.get('/UserImport/GetUserImportOptions').then((response) => {
            setUserImportOptions(response.data as UserImportFieldOptionsModel[]);
            setLoading(false);
        }).catch((e) => {
            console.error(e.message);
        });
    }, []);

    useEffect(() => {
        if(currentStep === UserImportStep.ReviewAndApplyChanges) {
            setLoading(false);
        }
    }, [currentStep])

    function refreshList(): void {
        httpCall.get('/UserImport?take=10&skip=0').then(({ data }: { data: any }) => {
            setImportHistory(data);
        });
    }

    function processUserUploadNotificationEvent(progress: UserUploadActionProgress): void {
        refreshList();
        if (progress.UserImportId === importId.current) {
            SetProgressReport(progress);
            if (progress.CurrentStage === "readyForImport" && currentStep !== UserImportStep.ReviewAndApplyChanges) {
                setLoading(false);
                SetStep(UserImportStep.ReviewAndApplyChanges);
            }
            if (progress.CurrentStage === "validating") {
                const { HeaderErrors, RowErrors, StageErrors } = progress.ErrorReport
                setErrors(!!(HeaderErrors.length + RowErrors.length + StageErrors.length))
            }
        }

        // once this state has been acknowledged via the websocket, stop tracking it in react
        if (["cancelled", "cancelling", "completed", "completing", "errored"].includes(progress.CurrentStage)) {
            cancelOrCompleteInFlightMap.current[progress.UserImportId] && delete cancelOrCompleteInFlightMap.current[progress.UserImportId];
        }
    }

    function SubmitUpload():Promise<boolean>{
        const formData = new FormData();
        formData.append('userImportType', uploadType.toString());
        formData.append('file', getFile.files[0]);

        httpCall.setHeaders({ "Content-Type": "multipart/form-data" });

        return httpCall.post('/UserImport/Upload', formData).then((uploadResponse) => {
            importId.current = parseInt(uploadResponse.data.userImportId);
            return true;
        }).catch((err) => {
            const { code, message } = err.response.data
            setResponseError({ message, code });
            return false;
        });
    }

    function NextStep() {
        // Validation
        if (!formValid) {
            return
        }

        if (currentStep === UserImportStep.ImportTypeSelection) {
            SetStep(UserImportStep.FileUpload);
            return;
        }

        if (currentStep === UserImportStep.FileUpload) {
            if(uploadType !== UserImportType.Fullsync){
                SubmitUpload().then((success) => {
                    if (success) {
                        setLoading(true);
                        SetStep(UserImportStep.Validation);
                    }
                });
            }else{
                SetStep(UserImportStep.UserOptions);
            }
            return;
        }

        if (currentStep === UserImportStep.UserOptions) {
            httpCall.post('/UserImport/SetUserImportOptions', userImportOptions).then((response) => {
                if (response.status !== 200) {
                    throw "Failed to Save"
                }

                SubmitUpload().then((success) => {
                    if (success) {
                        setLoading(true);
                        SetStep(UserImportStep.Validation);
                    }
                });
                return;
            }).catch(err => console.log(err));
        }

        if (currentStep === UserImportStep.Validation) {
            SetStep(UserImportStep.ReviewAndApplyChanges);
        }

        if (currentStep === UserImportStep.ReviewAndApplyChanges) {
            httpCall.post(`/UserImport/Complete/${importId.current}`, null).then((response) => {
                cancelOrCompleteInFlightMap.current[importId.current] = 'complete';
                if (response.status != 200) {
                    throw "Failed to Save"
                } else {
                    SetUploadModal(false);
                    SetStep(UserImportStep.ImportTypeSelection);
                }
            }).catch(err => console.log(err));
        }


    }
    function closeModal() {
        resetModal();
    }
    function Cancel() {
        if (importId.current && !errors) {
            // scope this to catch a race conditions issue, with reseting the modal
            const id = importId.current;
            cancelOrCompleteInFlightMap.current[id] = 'cancel'
            httpCall.post(`/UserImport/Cancel/${id}`, null)
                .catch(() => {
                    delete cancelOrCompleteInFlightMap.current[id];
                })
        }
        closeModal();
    }

    function resetModal(): void {
        SetStep(1);
        SetUploadModal(false);
        SetProgressReport(null);
        setErrors(false);
        importId.current = 0;
        setLoading(false);
        SetValid(false);
        setResponseError(undefined);
    }

    return (
        <div className="react-gen-container react-user-upload clearfix">

            <div>
                <div className="org-header-left user-upload-header">
                    <h1>
                        Bulk User Imports
                    </h1>
                    <ToolTipWrapper id="highlight-tooltip" className="tracks-side-tooltip user-upload-tooltip">
                        <ReactTooltip id="highlight-tooltip" place="top" className='thrive-tooltip' delayHide={1000} >
                            <span>Bulk manage users by uploading CSV files</span>
                        </ReactTooltip>
                    </ToolTipWrapper>
                </div>
                <div className="org-header-right user-upload-buttons">
                    <a target="_blank" href="/api/UserImport/DownloadTemplate" className="user-upload-btn-clear btn">Download Template</a>
                    <button
                        type="button"
                        className="btn-blue btn"
                        onClick={() => {
                            resetModal();
                            SetUploadModal(true);
                        }}
                    >
                        Import User CSV File
                    </button>
                </div>
            </div>

            <div className="clear"></div>

            <div className="upload-divider">&nbsp;</div>

            <UploadTable
                importHistory={importHistory}
                cancelOrCompleteRef={cancelOrCompleteInFlightMap}
            />

            <BulkImportUsersModal
                stepProps={{ userImportOptions, setFile, progressReport, SetValid, currentStep, setUserImportOptions, setUploadType, uploadType }}
                isOpen={isOpen}
                closeModal={closeModal}
                Cancel={Cancel}
                displayCloseButton={errors}
                formValid={formValid}
                NextStep={NextStep}
                Loading={loading}
                responseError={responseError}
                currentStep={currentStep}
                uploadType={uploadType}
                setUploadType={setUploadType}
            />

            {/* Details modal */}
            <UploadDetailsModal {...{ isDetailsOpen, SetDetailsModal, progressReport, }} />
        </div>
    )
}

export default UserUpload